import { Paper, Typography } from '@mui/material';
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { Organization } from 'sr-types/lib/organization/v1/graphql';
import AlternateIdForm from '../common/components/AlternateIdForm';
import FormNameEditor from '../common/components/FormNameEditor';
import { IMDB_LABEL, LINKEDIN_LABEL } from '../common/constants/alternateIdConstants';
import { CONSTANTS_ID } from '../common/constants/constants';
import { FormContainer } from '../common/form/FormContainer';
import { FormContext } from '../common/form/FormContext';
import FormItem from '../common/form/FormItem';
import FormPanel from '../common/form/FormPanel';
import FormWidget from '../common/form/FormWidget';
import useAISettings from '../common/hooks/useAISettings';
import { useI18n } from '../common/i18n/I18n';
import { Icons } from '../common/icons/Icons';
import useHistory from '../common/utils/useHistory';
import { getValidationErrorsForOrganization } from './organization';
import OrganizationOptionRenderer from './OrganizationOptionRenderer';
import { getOption } from './QuickAddOrganization';
import useOrganizationExpressions from './useOrganizationExpressions';

export const OrgTypeWithOneLocation = ['Campus'];

interface OrganizationProfileEditorProps {
    setDisableSave: Dispatch<SetStateAction<boolean>>;
    setValidationRules: Dispatch<SetStateAction<{}>>;
    conflictedEntity: Organization;
    setConflictedEntity: Dispatch<SetStateAction<Organization>>;
    errorKey: string;
    setErrorKey: Dispatch<SetStateAction<string>>;
}

export default function OrganizationProfileEditor({
    setDisableSave,
    setValidationRules,
    conflictedEntity,
    setConflictedEntity,
    errorKey,
    setErrorKey
}: OrganizationProfileEditorProps) {
    const { routeParams } = useHistory();
    const { entityId } = routeParams;
    const expressions =
        entityId === CONSTANTS_ID.createId
            ? {
                  field: 'identity',
                  value: {
                      values: [entityId]
                  },
                  mustNot: true
              }
            : undefined;
    const filters = useOrganizationExpressions(expressions);
    const isCreate = entityId === CONSTANTS_ID.createId;
    const { state, setState } = useContext(FormContext);
    const [openNameEditor, setOpenNameEditor] = useState(false);
    const [multipleLocationsAllowed, setmultipleLocationsAllowed] = useState(true);
    const isAIEnabled = useAISettings();
    const hiddenAlternateIdFields = [IMDB_LABEL, LINKEDIN_LABEL];
    const websiteHelperText = useI18n('form.organization.websiteURL.helperText');
    const [fieldSelected, setFieldSelected] = useState<string>(undefined);

    const onSave = () => {
        setOpenNameEditor(false);
    };

    const onClose = (previousValue) => {
        setState({ ...state, publicName: previousValue });
        setOpenNameEditor(false);
    };

    useEffect(() => {
        if (state.organizationTypes) {
            for (let organizationType of state.organizationTypes) {
                if (OrgTypeWithOneLocation.includes(organizationType)) {
                    setmultipleLocationsAllowed(false);
                    break;
                } else {
                    setmultipleLocationsAllowed(true);
                }
            }
        }
    }, [state.organizationTypes]);

    return (
        <Paper elevation={3} sx={{ paddingTop: 1, width: '100%' }}>
            <FormPanel>
                <FormContainer>
                    <FormItem>
                        {isCreate ? (
                            <FormWidget name="publicName" label="Name" />
                        ) : openNameEditor ? (
                            <FormNameEditor
                                value={state.publicName}
                                name={'publicName'}
                                label={'Name'}
                                onSave={onSave}
                                onClose={onClose}
                            />
                        ) : (
                            <>
                                <Typography fontSize={'medium'} fontWeight={'bold'} whiteSpace={'nowrap'}>
                                    {state.publicName}
                                </Typography>
                                {!openNameEditor && (
                                    <Icons.Edit
                                        color="primary"
                                        sx={{
                                            '&:hover': {
                                                cursor: 'pointer'
                                            }
                                        }}
                                        onClick={() => setOpenNameEditor(true)}
                                    />
                                )}
                            </>
                        )}
                    </FormItem>
                    <FormItem>
                        <FormWidget label="Website" name="websiteURL" helperText={isAIEnabled && websiteHelperText} />
                    </FormItem>
                    <FormItem>
                        <FormWidget
                            component="MetadataAutocomplete"
                            compressed
                            label="Organization types"
                            limitTags={3}
                            metadataName="OrganizationType"
                            multiple
                            name="organizationTypes"
                        />
                    </FormItem>
                    <FormItem>
                        <FormWidget
                            component="Location"
                            compressed
                            label="Locations"
                            appendTimeZone={true}
                            multiple={multipleLocationsAllowed}
                            name="locations"
                        />
                    </FormItem>
                    <FormItem children={undefined} />
                    <FormItem>
                        <FormWidget
                            component="ReferenceAutocomplete"
                            name="parentOrganization"
                            label="Parent Organization"
                            entity="Organization"
                            OptionRenderer={OrganizationOptionRenderer}
                            getOption={getOption}
                            filter={filters}
                            preload={false}
                        />
                    </FormItem>
                    <FormItem>
                        <FormWidget component="TextArea" label="Headline" name="summary.headline" />
                    </FormItem>
                    <AlternateIdForm
                        setIsSaveDisabled={setDisableSave}
                        setDynamicValidationRules={setValidationRules}
                        getValidationErrors={getValidationErrorsForOrganization}
                        hiddenFields={hiddenAlternateIdFields}
                        setConflictedEntity={setConflictedEntity}
                        conflictedEntity={conflictedEntity}
                        fieldSelected={fieldSelected}
                        setFieldSelected={setFieldSelected}
                        errorKey={errorKey}
                        setErrorKey={setErrorKey}
                    />
                </FormContainer>
            </FormPanel>
        </Paper>
    );
}
